/**
 * 본문 속 외부 링크 판별 — 미리보기 카드(LinkPreview)와 본문 링크화(linkify)가 같은 규칙을 쓴다.
 */

const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

/**
 * 본문에서 잡힌 URL 끝의 군더더기를 걷어낸다.
 *  - 문장부호: "…참고하세요: https://a.com/x." → 마침표 제외
 *  - 짝 없는 닫는 괄호: "(https://a.com/x)" → ")" 제외, "https://ko.wikipedia.org/wiki/A_(B)"는 유지
 *  - 붙여 쓴 조사: "https://youtu.be/abc를" → "를" 제외. 단 "/w/대한민국"처럼 경로가 한글로 시작하면 유지
 */
export function trimUrl(raw: string): string {
  let url = raw;
  for (;;) {
    const before = url;
    url = url.replace(/[.,;:!?…'"“”‘’]+$/, '');
    const last = url.slice(-1);
    if (CLOSERS[last]) {
      const opens = url.split(CLOSERS[last]).length - 1;
      const closes = url.split(last).length - 1;
      if (closes > opens) url = url.slice(0, -1);
    }
    const hangulTail = url.match(/[가-힣ㄱ-ㅎㅏ-ㅣ]+$/);
    if (hangulTail) {
      const prev = url.charAt(url.length - hangulTail[0].length - 1);
      if (!/[/=_\-#?&]/.test(prev)) url = url.slice(0, -hangulTail[0].length);
    }
    if (url === before) return url;
  }
}

const URL_RE = /https?:\/\/[^\s<>"'`]+/gi;

/** 본문 속 http(s) 링크(중복 제거, 등장 순서 유지). */
export function extractUrls(text: string): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  for (const m of text.match(URL_RE) || []) {
    const url = trimUrl(m);
    try {
      new URL(url);
    } catch {
      continue;
    }
    seen.add(url);
  }
  return Array.from(seen);
}

/** 제목이 링크 하나뿐인지("https://youtu.be/abc..." 처럼 잘린 것 포함) — 카드가 대신 제목을 보여 준다. */
export function isUrlOnlyText(text: string | null | undefined): boolean {
  return !!text && /^\s*https?:\/\/\S+\s*$/.test(text);
}

/** 미리보기 카드가 대신 보여 주는 링크 줄(그 링크만 있는 줄)을 발췌에서 뺀다. */
export function withoutUrlLines(text: string, urls: string[]): string {
  if (!urls.length) return text;
  const set = new Set(urls);
  return text
    .split('\n')
    .filter((line) => !set.has(trimUrl(line.trim())))
    .join('\n')
    .trim();
}

/** 응답이 오기 전 자리 잡기용 — 동영상 링크면 16:9(쇼츠는 9:16) 자리를 미리 확보해 카드가 튀지 않게 한다. */
export function videoShape(url: string): 'wide' | 'tall' | null {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase().replace(/^(www|m|music)\./, '');
    if (host === 'youtube.com' && u.pathname.startsWith('/shorts/')) return 'tall';
    if (host === 'youtu.be' && u.pathname.length > 1) return 'wide';
    if (host === 'youtube.com' && /^\/(watch|live\/|embed\/)/.test(u.pathname)) return 'wide';
    if ((host === 'vimeo.com' || host === 'player.vimeo.com') && /\d{6,}/.test(u.pathname)) return 'wide';
  } catch {
    // 잘못된 URL
  }
  return null;
}
